import { useRouter, useSearchParams } from 'next/navigation'

export const TableHeaderCell = ({
  children,
  sortKey,
  sortable = false,
  tableNamespace = 'table',
  align = 'left',
  className = '',
}: TableHeaderCellProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()

  // Get current sort state from URL params
  const currentSortField = searchParams.get(`${tableNamespace}_sort`)
  const currentSortDirection = searchParams.get(`${tableNamespace}_dir`)

  // Determine if this column is currently sorted
  const isCurrentlySorted = currentSortField === sortKey
  const sortDirection = isCurrentlySorted ? currentSortDirection : null

  const handleSort = () => {
    if (!sortable || !sortKey) return

    const params = new URLSearchParams(searchParams.toString())

    if (isCurrentlySorted) {
      // Toggle direction if same field
      const newDirection = currentSortDirection === 'asc' ? 'desc' : 'asc'
      params.set(`${tableNamespace}_sort`, sortKey)
      params.set(`${tableNamespace}_dir`, newDirection)
    } else {
      // New field, start with ascending
      params.set(`${tableNamespace}_sort`, sortKey)
      params.set(`${tableNamespace}_dir`, 'asc')
    }

    // Update URL without page reload
    router.replace(`?${params.toString()}`, { scroll: false })
  }

  const baseClasses = 'px-4 py-3 text-base font-semibold text-gray-700'
  const clickableClasses = sortable
    ? 'cursor-pointer hover:text-vi-red hover:underline select-none'
    : ''
  const alignClasses = align === 'right' ? 'text-right' : 'text-left'
  const contentAlignClasses =
    align === 'right' ? 'justify-end' : 'justify-start'

  return (
    <th
      scope="col"
      className={`${baseClasses} ${alignClasses} ${clickableClasses} ${className}`}
      onClick={handleSort}
    >
      <div className={`flex items-center gap-1 ${contentAlignClasses}`}>
        <span>{children}</span>
        {sortable && (
          <span className="flex flex-col">
            <svg
              className={`w-4 h-4 ${sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-400'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            <svg
              className={`w-4 h-4 -mt-2 ${sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-400'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        )}
      </div>
    </th>
  )
}

interface TableHeaderCellProps {
  children: React.ReactNode
  sortKey?: string
  sortable?: boolean
  tableNamespace?: string
  align?: 'left' | 'right'
  className?: string
}
